<!-- About Us Page - 基于 Test Page 设计 -->
<template>
  <CommonPages id="about-us" :class="['about-us-page']" :titlePrefix="pageMetaTitle"
    :metaDescription="pageMetaDescription">

    <!-- 主要内容区域 -->
    <section class="about-us-content">
      <div class="container">
        <!-- 标题 -->
        <h1 class="about-us-content__title">About Us</h1>
        <!-- 描述文字 -->
        <div class="about-us-content__desc" v-html="aboutUsBody"></div>

        <!-- 图片展示区域 -->
        <div class="about-us-content__gallery">
          <div
            v-for="galleryImage in galleryImages"
            :key="galleryImage.src"
            class="about-us-content__gallery-item">
            <img :src="galleryImage.src" :alt="galleryImage.alt">
          </div>
        </div>
      </div>
    </section>

  </CommonPages>
</template>

<script>
import common from '@/mixin/common.js'
import { mapGetters } from 'vuex';
import { getContentTextMap, getText, renderTemplate } from '@/utils/contentText';
import { stripLeadingRichTextTitle } from '@/utils/richTextTitle';

// 核心组件
import CommonPages from '@/pages/components/page_common.vue'

const ABOUT_GALLERY_IMAGES = [
  {
    src: require('@/assets/images/about-gallery/about-gallery-1.png'),
    alt: 'New York skyline'
  },
  {
    src: require('@/assets/images/about-gallery/about-gallery-2.png'),
    alt: 'Tower Bridge riverside view'
  },
  {
    src: require('@/assets/images/about-gallery/about-gallery-3.png'),
    alt: 'Eiffel Tower street view'
  },
  {
    src: require('@/assets/images/about-gallery/about-gallery-4.png'),
    alt: 'Sydney Opera House waterfront view'
  },
  {
    src: require('@/assets/images/about-gallery/about-gallery-5.png'),
    alt: 'Temple with cherry blossoms'
  }
];

const ABOUT_US_BODY = `About Us<br>Welcome to {{SITE_NAME}}. We provide a curated, data-driven perspective on urban livability across 50 of the world’s most prominent cities.<br><br>Our Mission<br>In an increasingly mobile world, choosing where to live, work, or travel is a significant decision. Our mission is to simplify this process by breaking down complex urban data into a clear, comparable, and intuitive scoring system. We move beyond simple rankings to provide a nuanced look at what makes a city truly functional and fulfilling.<br><br>The \"50 Cities\" Selection<br>Unlike platforms that attempt to rank thousands of locations, we have intentionally focused our analysis on 50 global hubs. These cities were selected based on their international significance, economic impact, and cultural diversity. By narrowing our focus, we ensure that our evaluations remain high-quality, relevant, and deeply analyzed.<br><br>Our Methodology: The Five Indicators<br>We evaluate each city across five core dimensions to provide a balanced overview of urban life:<br>- Air Quality: Measuring environmental health, pollution levels, and access to clean, breathable air.<br>- Safety: Assessing crime rates, personal security, and the overall stability of the urban environment.<br>- Living Cost: Analyzing the relative affordability of daily life, from essential goods to local services.<br>- Healthcare: Reviewing the accessibility, quality, and efficiency of medical facilities and public health systems.<br>- Transport: Evaluating infrastructure, the efficiency of public transit networks, and ease of mobility.<br><br>Our Approach<br>We believe that numbers only tell half the story. That is why every city on our list features a unique qualitative evaluation for each metric. Our ratings are the result of synthesized data and editorial analysis, designed to give you a \"snapshot\" of a city\\'s soul as much as its statistics.<br><br>Privacy & Transparency<br>We are a privacy-first platform. We do not use cookies, we do not track your data, and we do not require registration. Our goal is simply to provide a clean, ad-free environment for you to explore the world’s greatest cities.`;

export default {
  name: 'AboutUs',
  components: {
    CommonPages
  },
  mixins: [common],
  data () {
    return {
      galleryImages: ABOUT_GALLERY_IMAGES
    };
  },
  computed: {
    ...mapGetters([
      'deviceType',
      'domainConfig'
    ]),
    contentText () {
      return getContentTextMap(this.domainConfig);
    },
    aboutUsBody () {
      const template = getText(this.contentText, 'ABOUT_US_BODY', ABOUT_US_BODY);
      const rendered = renderTemplate(template, {
        SITE_NAME: this.domainConfig?.siteName || 'SITE_NAME',
        CONTACT_EMAIL: this.domainConfig?.IAMEMAIL || this.contentText.CONTACT_EMAIL || 'CONTACT_EMAIL'
      });
      return stripLeadingRichTextTitle(rendered, 'About Us');
    },
    pageMetaTitle () {
      return getText(this.contentText, 'ABOUT_META_TITLE', 'About Us');
    },
    pageMetaDescription () {
      return getText(this.contentText, 'ABOUT_META_DESCRIPTION', 'Welcome to our site. We have created this page to introduce ourselves to you and to let you know what it is we do around here.');
    }
  },
  async mounted () {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AboutUs] Component mounted`);
    }
  },
  methods: {
    // 方法可以在这里添加
  }
};
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

// 颜色变量
$color-bg: #fff9e1; // 米黄色背景
$color-text-dark: #000000; // 深色文字
$color-text-muted: rgba(0, 0, 0, 0.5); // 灰色文字
$color-border: rgba(0, 0, 0, 0.1); // 边框色
$font-family: 'IBM Plex Serif', Georgia, serif;

.about-us-page {
	font-family: $font-family;
	background-color: $color-bg;
	min-height: 100vh;

	// 内容区域样式
	.about-us-content {
		padding: rem(60) 0;

		.container {
			max-width: rem(1080);
			margin: 0 auto;
			padding: 0 rem(20);
		}

		&__title {
			font-size: rem(56);
			font-weight: 600;
			font-family: $font-family;
			color: $color-text-dark;
			margin-bottom: rem(32);
			line-height: rem(60);
		}

		&__desc {
			margin-bottom: rem(40);
			font-size: rem(13);
			font-family: $font-family;
			font-weight: 400;
			line-height: rem(24);
			color: $color-text-dark;

			p {
				margin-bottom: rem(16);

				&:last-child {
					margin-bottom: 0;
				}
			}
		}

		&__gallery {
			display: flex;
			justify-content: space-between;
			gap: rem(24);
			flex-wrap: wrap;

			&-item {
				flex: 1;
				min-width: rem(150);
				max-width: rem(196);
				aspect-ratio: 1;
				border-radius: rem(24);
				overflow: hidden;
				border: 1px solid $color-border;
				display: flex;
				align-items: stretch;
				justify-content: stretch;

				img {
					display: block;
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}
		}
	}
}

// 响应式适配
@media (max-width: 768px) {
	.about-us-page {
		.about-us-content {
			padding: rem(40) 0;

			&__title {
				font-size: rem(40);
				line-height: rem(44);
			}

			&__gallery {
				&-item {
					min-width: calc(50% - #{rem(12)});
					max-width: calc(50% - #{rem(12)});
				}
			}
		}
	}
}
</style>
